import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { FormField, form } from '@angular/forms/signals';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import {
  applyCreatorLookupToRegistrationModel,
  buildMobileE164,
  CreatorRegistrationSnapshot,
  CURRENCY_OPTIONS,
  FirebaseCreatorLookupResponse,
  getPhoneCountryPreset,
  PHONE_COUNTRY_PRESETS,
  PhoneCountryCode,
  registrationSnapshotsEqual,
  toRegistrationSnapshot,
  validateCreatorName,
  validatePhoneLocal,
} from './creator-registration.types';
import { AdminCreatorRegistrationService } from '../../services/admin-creator-registration.service';

@Component({
  selector: 'admin-tool-creator-manual-registration',
  imports: [FormField, RouterLink],
  templateUrl: './creator-manual-registration.html',
  styleUrl: './creator-manual-registration.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorManualRegistration {
  readonly countryPresets = PHONE_COUNTRY_PRESETS;
  readonly currencyOptions = CURRENCY_OPTIONS;

  lookupLoading = signal(false);
  actionLoading = signal(false);
  lookupResult = signal<FirebaseCreatorLookupResponse | null>(null);
  lookupError = signal<string | null>(null);
  loadedFormSnapshot = signal<CreatorRegistrationSnapshot | null>(null);
  lastRegisteredUid = signal<string | null>(null);
  lastActionCreated = signal<boolean | null>(null);

  registrationModel = signal({
    blazeFirebaseUid: '',
    email: '',
    firstName: '',
    lastName: '',
    phoneCountry: 'GB' as PhoneCountryCode,
    phoneLocal: '',
    currencyCode: 'gbp' as 'gbp' | 'usd' | 'eur',
  });

  registrationForm = form(this.registrationModel);

  selectedPhonePreset = computed(() => getPhoneCountryPreset(this.registrationModel().phoneCountry));

  isUpdateMode = computed(() => this.loadedFormSnapshot() !== null);

  submitButtonLabel = computed(() =>
    this.isUpdateMode() ? 'Update creator' : 'Create creator',
  );

  formHasChanges = computed(() => {
    const baseline = this.loadedFormSnapshot();
    if (!baseline) {
      return true;
    }
    return !registrationSnapshotsEqual(baseline, toRegistrationSnapshot(this.registrationModel()));
  });

  submitDisabled = computed(() => {
    if (this.actionLoading()) {
      return true;
    }
    if (this.isUpdateMode()) {
      return !this.formHasChanges();
    }
    return false;
  });

  private destroyRef = inject(DestroyRef);
  private registrationService = inject(AdminCreatorRegistrationService);
  private alertService = inject(AlertService);
  private toastNotificationService = inject(ToastNotificationService);

  onPhoneCountryChange(code: string): void {
    const preset = getPhoneCountryPreset(code as PhoneCountryCode);
    this.registrationModel.update((model) => ({
      ...model,
      phoneCountry: preset.countryCode,
      currencyCode: preset.defaultCurrency,
    }));
  }

  onCurrencyChange(code: string): void {
    this.registrationModel.update((model) => ({
      ...model,
      currencyCode: code as 'gbp' | 'usd' | 'eur',
    }));
  }

  runLookup(): void {
    const uid = this.registrationModel().blazeFirebaseUid.trim();
    if (!uid) {
      void this.toastNotificationService.alert({
        title: 'Firebase UID required',
        message: 'Enter a Firebase UID before looking up.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    this.lookupLoading.set(true);
    this.lookupError.set(null);
    this.lookupResult.set(null);
    this.loadedFormSnapshot.set(null);
    this.lastRegisteredUid.set(null);
    this.lastActionCreated.set(null);

    this.registrationService
      .lookupFirebase(uid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.lookupLoading.set(false);
          this.lookupResult.set(result);
          this.registrationModel.update((model) => {
            const next = applyCreatorLookupToRegistrationModel(model, result);
            this.loadedFormSnapshot.set(
              result.creatorExists ? toRegistrationSnapshot(next) : null,
            );
            return next;
          });
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.lookupLoading.set(false);
          const message = this.extractErrorMessage(err);
          this.lookupError.set(message);
          void this.toastNotificationService.alert({
            title: 'Lookup failed',
            message,
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  async runRegister(): Promise<void> {
    const model = this.registrationModel();
    const uid = model.blazeFirebaseUid.trim();
    const email = model.email.trim();
    const firstNameError = validateCreatorName(model.firstName, 'First name');
    const lastNameError = validateCreatorName(model.lastName, 'Last name');
    const phoneError = validatePhoneLocal(model.phoneCountry, model.phoneLocal);

    if (!uid) {
      void this.toastNotificationService.alert({
        title: 'Firebase UID required',
        message: 'Enter the Firebase UID for this creator.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    if (!email) {
      void this.toastNotificationService.alert({
        title: 'Email required',
        message: 'Enter the creator email address.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    if (firstNameError || lastNameError || phoneError) {
      void this.toastNotificationService.alert({
        title: 'Validation error',
        message: firstNameError ?? lastNameError ?? phoneError ?? 'Check the form fields.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    const lookup = this.lookupResult();
    if (lookup && !lookup.exists) {
      void this.toastNotificationService.alert({
        title: 'Firebase user not found',
        message: 'Run lookup first or verify the UID exists in Firebase Auth.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    const creatorExists = lookup?.creatorExists ?? false;
    const confirmed = await this.alertService.confirm({
      title: creatorExists ? 'Update creator record' : 'Register creator',
      message: creatorExists
        ? 'A creator record already exists for this UID. The form values will be saved over the existing record.'
        : 'Create a new Shout creator record for this Firebase user?',
      confirmText: creatorExists ? 'Update' : 'Register',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: creatorExists ? 'warning' : 'info',
    });

    if (!confirmed) {
      return;
    }

    const preset = getPhoneCountryPreset(model.phoneCountry);
    const body = {
      blazeFirebaseUid: uid,
      email,
      firstName: model.firstName.trim(),
      lastName: model.lastName.trim(),
      mobile: buildMobileE164(model.phoneCountry, model.phoneLocal),
      countryCode: preset.countryCode,
      currencyCode: model.currencyCode,
    };

    this.actionLoading.set(true);

    this.registrationService
      .register(body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.actionLoading.set(false);
          this.lastRegisteredUid.set(response.creator.blazeFirebaseUid);
          this.lastActionCreated.set(response.created);
          this.lookupResult.update((current) =>
            current
              ? { ...current, creatorExists: true }
              : { exists: true, creatorExists: true, email: response.creator.email },
          );
          this.loadedFormSnapshot.set(toRegistrationSnapshot(this.registrationModel()));
          void this.toastNotificationService.alert({
            title: response.created ? 'Creator registered' : 'Creator updated',
            message: `${response.creator.firstName} ${response.creator.lastName} (${response.creator.email})`,
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.actionLoading.set(false);
          void this.toastNotificationService.alert({
            title: 'Registration failed',
            message: this.extractErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  private extractErrorMessage(err: { error?: { message?: string | string[] }; message?: string }): string {
    const nested = err.error?.message;
    if (Array.isArray(nested)) {
      return nested.join(', ');
    }
    if (typeof nested === 'string' && nested.length > 0) {
      return nested;
    }
    return err.message ?? 'An unexpected error occurred.';
  }
}
